import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Book, CopyrightSettings } from '../types';
import { getGraphQLClient, GET_BOOK, UPDATE_BOOK } from '../lib/graphql';
import { captureException } from '../lib/sentry';
import { FrontMatterEditorLayout } from '../components/front-matter/FrontMatterEditorLayout';
import { getCopyrightTemplates, CopyrightTemplate } from '../lib/frontMatterTemplates';
import { FormLabel } from '../components/common';

export default function CopyrightEditorPage() {
  const { t } = useTranslation();
  const { bookId } = useParams<{ bookId: string }>();
  const [book, setBook] = useState<Book | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [isVisible, setIsVisible] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [holder, setHolder] = useState('');
  const [customText, setCustomText] = useState('');
  const [publisher, setPublisher] = useState('');
  const [edition, setEdition] = useState('');
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  useEffect(() => {
    const loadBook = async () => {
      if (!bookId) return;

      try {
        const client = getGraphQLClient();
        const data = await client.request<{ book: Book }>(GET_BOOK, { id: bookId });
        setBook(data.book);

        // Initialize form state from book settings
        const settings = data.book.frontMatterSettings?.copyright;
        setIsVisible(settings?.visible ?? true);
        setYear(settings?.year ?? new Date().getFullYear());
        setHolder(settings?.holder ?? '');
        setCustomText(settings?.customText ?? '');
        setPublisher(settings?.publisher ?? '');
        setEdition(settings?.edition ?? '');
        setShowDisclaimer(settings?.showDisclaimer ?? true);
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      } finally {
        setIsLoading(false);
      }
    };

    loadBook();
  }, [bookId, t]);

  const handleSave = async () => {
    if (!bookId) return;

    setIsSaving(true);
    setError(null);

    try {
      const client = getGraphQLClient();
      const copyrightSettings: CopyrightSettings = {
        visible: isVisible,
        year,
        holder: holder || undefined,
        customText: customText || undefined,
        publisher: publisher || undefined,
        edition: edition || undefined,
        showDisclaimer,
      };

      await client.request(UPDATE_BOOK, {
        id: bookId,
        frontMatterSettings: {
          copyright: copyrightSettings,
        },
      });
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleTemplateSelect = (template: CopyrightTemplate) => {
    setCustomText(t(template.contentKey));
  };

  const templates = book ? getCopyrightTemplates(book.bookType) : [];
  const copyrightHolder = holder || book?.user?.name || 'Author';

  return (
    <FrontMatterEditorLayout
      title={t('frontMatter.copyright')}
      bookId={bookId!}
      isLoading={isLoading}
      isSaving={isSaving}
      isVisible={isVisible}
      onVisibilityChange={setIsVisible}
      onSave={handleSave}
      error={error}
      preview={
        <div className="text-left max-w-md text-sm text-gray-500 dark:text-gray-500 space-y-3">
          <p className="font-serif italic">{book?.title || 'Book Title'}</p>
          <p>
            {t('frontMatter.copyrightEditor.copyrightNotice', { year, holder: copyrightHolder })}
          </p>
          {customText && <p className="text-xs leading-relaxed">{customText}</p>}
          {showDisclaimer && book?.bookType?.toLowerCase() === 'fiction' && !customText && (
            <p className="text-xs leading-relaxed">
              This is a work of fiction. Names, characters, places, and incidents either are
              products of the author&apos;s imagination or are used fictitiously.
            </p>
          )}
          {publisher && <p className="text-xs">Published by {publisher}</p>}
          {edition && <p className="text-xs">{edition}</p>}
          <p className="text-xs pt-2">{t('frontMatter.copyrightEditor.createdWithNextPage')}</p>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Year */}
        <div>
          <FormLabel>{t('frontMatter.copyrightYear')}</FormLabel>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())}
            min={1900}
            max={2100}
            className="w-32 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Copyright holder */}
        <div>
          <FormLabel>{t('frontMatter.copyrightHolder')}</FormLabel>
          <input
            type="text"
            value={holder}
            onChange={(e) => setHolder(e.target.value)}
            placeholder={book?.user?.name || t('frontMatter.authorName')}
            className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Disclaimer templates */}
        <div>
          <FormLabel>{t('frontMatter.disclaimerTemplate')}</FormLabel>
          <div className="grid grid-cols-1 gap-2">
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateSelect(template)}
                className="text-left px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
              >
                <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                  {t(template.contentKey)}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Custom text */}
        <div>
          <FormLabel>{t('frontMatter.customDisclaimerText')}</FormLabel>
          <textarea
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            placeholder={t('frontMatter.customDisclaimerPlaceholder')}
            rows={3}
            className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Publisher */}
        <div>
          <FormLabel>{t('frontMatter.publisher')}</FormLabel>
          <input
            type="text"
            value={publisher}
            onChange={(e) => setPublisher(e.target.value)}
            placeholder={t('frontMatter.publisherPlaceholder')}
            className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Edition */}
        <div>
          <FormLabel>{t('frontMatter.edition')}</FormLabel>
          <input
            type="text"
            value={edition}
            onChange={(e) => setEdition(e.target.value)}
            placeholder={t('frontMatter.copyrightEditor.editionPlaceholder')}
            className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Show default disclaimer toggle */}
        {!customText && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowDisclaimer(!showDisclaimer)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                showDisclaimer ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  showDisclaimer ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {t('frontMatter.showDefaultDisclaimer')}
            </span>
          </div>
        )}
      </div>
    </FrontMatterEditorLayout>
  );
}
